import { useCurrentUser } from 'providers/UserProvider';
import { useBlockLockStatus } from 'components/editor/hooks/useBlockLockStatus';
import { useNewBlockStyleSettings } from 'components/editor/hooks/useNewBlockSettings';
import { useOpenBlockSettings } from '../BlockSettings/useOpenBlockSettings';
import type { MenuItemType } from './CustomContextMenuContainer.tsx';
import { GridBlockType } from 'services/entities/BlockTypes';
import { useAppSelector } from 'components/editor/grid/reduxStore/Store';
import { selectSectionIdByBlockId } from 'components/editor/grid/reduxStore/editorSlice';

type UseBlockLockContextMenuOptionParams = {
  blockId: string;
  blockType?: GridBlockType;
  isEditMode?: boolean;
  setIsEditMode?: (isEditMode: boolean) => void;
};

export const useBlockLockContextMenuOption = ({
  blockId,
  blockType,
  isEditMode,
  setIsEditMode,
}: UseBlockLockContextMenuOptionParams): MenuItemType => {
  const sectionId = useAppSelector((state) => selectSectionIdByBlockId(state, blockId));
  const { getBlockStyleById, addBlock, updateBlockStyleWithUndo } = useNewBlockStyleSettings(blockId, sectionId ?? '');
  const { data: currentUser } = useCurrentUser();
  const userId = currentUser.id;
  const { isLocked } = useBlockLockStatus(blockId);
  const { openBlockSettings, closeBlockSettings, openTableDefaultSettings } = useOpenBlockSettings();

  const handleToggleLock = () => {
    const newLockedStatus = isLocked ? null : userId;

    const blockStyle = getBlockStyleById(blockId);
    if (!blockStyle) {
      addBlock(blockId, false);
    }

    updateBlockStyleWithUndo({ lockedBy: newLockedStatus });

    if (isLocked) {
      if (blockType) {
        switch (blockType) {
          case GridBlockType.TEXT:
          case GridBlockType.IMAGE:
          case GridBlockType.VIDEO:
          case GridBlockType.SHAPE:
            openBlockSettings(blockType, blockId);
            break;
          case GridBlockType.TABLE:
            openTableDefaultSettings(blockId);
            break;
        }
      }
    } else {
      closeBlockSettings();
      if (isEditMode && setIsEditMode) {
        setIsEditMode(false);
      }
    }
  };

  return {
    type: 'item' as const,
    title: isLocked ? 'Unlock block' : 'Lock block',
    callback: handleToggleLock,
  };
};
